import React, { forwardRef } from 'react';
import Heading from '@atlaskit/heading';
import { Box, Inline, Stack, xcss } from '@atlaskit/primitives';
import { getAppearanceIconStyles } from './internal/appearance-icon';
var containerStyles = xcss({
  wordBreak: 'break-word',
  borderRadius: 'border.radius'
});
var bleedStyles = xcss({
  display: 'flex',
  marginBlock: 'space.negative.025'
});
var contentStyles = xcss({
  color: 'color.text',
  font: 'font.body'
});

/**
 * __Section message__
 *
 * A section message is used to alert users to a particular section of the screen.
 *
 * - [Examples](https://atlassian.design/components/section-message/examples)
 * - [Code](https://atlassian.design/components/section-message/code)
 * - [Usage](https://atlassian.design/components/section-message/usage)
 */
var SectionMessage = /*#__PURE__*/forwardRef(function SectionMessage(_ref, ref) {
  var children = _ref.children,
    _ref$appearance = _ref.appearance,
    appearance = _ref$appearance === void 0 ? 'information' : _ref$appearance,
    actions = _ref.actions,
    title = _ref.title,
    icon = _ref.icon,
    testId = _ref.testId;
  var _getAppearanceIconSty = getAppearanceIconStyles(appearance, icon),
    primaryColor = _getAppearanceIconSty.primaryIconColor,
    secondaryColor = _getAppearanceIconSty.backgroundColor,
    Icon = _getAppearanceIconSty.Icon;
  var actionElements = actions && actions.type === React.Fragment ? actions.props.children : actions;
  var actionsArray = React.Children.toArray(actionElements);
  return /*#__PURE__*/React.createElement(Box, {
    as: "section",
    backgroundColor: appearanceMap[appearance],
    padding: "space.200",
    testId: testId,
    ref: ref,
    xcss: containerStyles
  }, /*#__PURE__*/React.createElement(Inline, {
    space: "space.200",
    alignBlock: "stretch"
  }, /*#__PURE__*/React.createElement(Box, {
    xcss: bleedStyles
  }, /*#__PURE__*/React.createElement(Icon, {
    size: "medium",
    primaryColor: primaryColor,
    secondaryColor: secondaryColor
    // props for new icon
    ,
    LEGACY_size: "medium",
    color: primaryColor,
    spacing: "spacious"
  })), /*#__PURE__*/React.createElement(Stack, {
    space: "space.100",
    testId: testId && "".concat(testId, "--content")
  }, !!title && /*#__PURE__*/React.createElement(Heading, {
    as: "h2",
    size: "small"
  }, title), /*#__PURE__*/React.createElement(Box, {
    xcss: contentStyles
  }, children), actionsArray.length > 0 && /*#__PURE__*/React.createElement(Inline, {
    shouldWrap: true,
    testId: testId && "".concat(testId, "--actions"),
    separator: "\xB7",
    space: "space.100",
    rowSpace: "space.0",
    role: "list"
  }, actionsArray.map(function (action, id) {
    return /*#__PURE__*/React.createElement(Inline, {
      role: "listitem",
      key: id
    }, action);
  })))));
});
var appearanceMap = {
  information: 'color.background.information',
  warning: 'color.background.warning',
  error: 'color.background.danger',
  success: 'color.background.success',
  discovery: 'color.background.discovery'
};
SectionMessage.displayName = 'SectionMessage';
export default SectionMessage;